import type { QueryKey } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { CreatorTagBatchPayload, CreatorTagColor, CreatorTagImportResult } from "@unbox/api";
import { handleError } from "@/api/error";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useCreatorTags = () => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useQuery({
    queryKey: ["creator-tags", merchantId, workspaceId],
    queryFn: async () => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].$get({
            param: { workspaceId, merchantId },
          })
        : await client.merchant[":merchantId"]["creator-tags"].$get({
            param: { merchantId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!merchantId,
  });
};

export const useCreatorTagAssignedCount = (tagId: string, enabled = true) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["creator-tags", scope.merchantId, scope.workspaceId, tagId, "count"],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"][":tagId"].count.$get({
            param: { ...scope.param, tagId },
          })
        : await client.merchant[":merchantId"]["creator-tags"][":tagId"].count.$get({
            param: { ...scope.param, tagId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!scope.merchantId && !!tagId,
  });
};

export const useImportCreatorTagsCsv = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (csv: string): Promise<CreatorTagImportResult> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].import.$post({
            param: scope.param,
            json: { csv },
          })
        : await client.merchant[":merchantId"]["creator-tags"].import.$post({
            param: scope.param,
            json: { csv },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-tags"] });
    },
  });
};

// Fetches the import template CSV and triggers a browser download. The filename
// is taken from the server's Content-Disposition header.
export const useDownloadCreatorTagTemplate = () => {
  const scope = useScopedMerchantClient();

  return async () => {
    const res = scope.workspaceId
      ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].template.$get({
          param: scope.param,
        })
      : await client.merchant[":merchantId"]["creator-tags"].template.$get({ param: scope.param });
    if (!res.ok) throw await handleError(res);

    const blob = await res.blob();
    const filename =
      res.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ?? "creator-tags-template.csv";
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
};

export const useBatchSaveCreatorTags = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: CreatorTagBatchPayload) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].batch.$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"]["creator-tags"].batch.$post({
            param: scope.param,
            json: payload,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-tags"] });
    },
  });
};

// ---------------------------------------------------------------------------
// Inline tag picker on creator rows (UNB-1733)
//
// The creator explorer list response (`useCreatorsMetrics`, key
// ["creator-overview", ...], shape { creators }) carries only assigned tag IDs
// per row. The table resolves names/colours from the live tag list
// (`useCreatorTags`), so tag edits (e.g. recolouring) reflect without
// refetching rows. Optimistic updates patch the row's `tagIds` directly so pills
// stay correct even when rows unmount/remount during pagination.
// ---------------------------------------------------------------------------

type TagRow = { tagOpenId: string | null; tagIds: string[] };
type RecommendationRow = { tiktokCreatorOpenId: string; tagIds?: string[] };
type BlockedCreatorRow = { creatorOpenId: string; tagIds: string[] };
type CandidateRow = { tikTokCreatorOpenId: string; tagIds: string[] };

const isCreatorOverviewQuery = (key: QueryKey): boolean => key[0] === "creator-overview";
const isMerchantRecommendationsQuery = (key: QueryKey): boolean => key[0] === "merchant-recommendations";
const isBlockedCreatorsQuery = (key: QueryKey): boolean => key[0] === "blocked-creators";
const isCandidatesQuery = (key: QueryKey): boolean => key[0] === "candidates";
/** All list shapes carry per-creator `tagIds` and render the shared tags cell. */
const isTaggableRowQuery = (key: QueryKey): boolean =>
  isCreatorOverviewQuery(key) ||
  isMerchantRecommendationsQuery(key) ||
  isBlockedCreatorsQuery(key) ||
  isCandidatesQuery(key);

/**
 * Apply `update` to the assigned tag IDs of the row matching `tiktokCreatorOpenId` across
 * every cached list that renders the tags cell: the creator explorer
 * (`{ creators }`) and the recommendations / "Matches" table (`{ recommendations }`).
 */
const patchCreatorRowTagIds = (tiktokCreatorOpenId: string, update: (tagIds: string[]) => string[]) => {
  queryClient.setQueriesData<{ creators: TagRow[] }>({ predicate: (q) => isCreatorOverviewQuery(q.queryKey) }, (old) =>
    old
      ? {
          ...old,
          creators: old.creators.map((row) =>
            row.tagOpenId === tiktokCreatorOpenId ? { ...row, tagIds: update(row.tagIds ?? []) } : row,
          ),
        }
      : old,
  );
  queryClient.setQueriesData<{ recommendations: RecommendationRow[] }>(
    { predicate: (q) => isMerchantRecommendationsQuery(q.queryKey) },
    (old) =>
      old
        ? {
            ...old,
            recommendations: old.recommendations.map((row) =>
              row.tiktokCreatorOpenId === tiktokCreatorOpenId ? { ...row, tagIds: update(row.tagIds ?? []) } : row,
            ),
          }
        : old,
  );
  queryClient.setQueriesData<{ items: BlockedCreatorRow[] }>(
    { predicate: (q) => isBlockedCreatorsQuery(q.queryKey) },
    (old) =>
      old
        ? {
            ...old,
            items: old.items.map((row) =>
              row.creatorOpenId === tiktokCreatorOpenId ? { ...row, tagIds: update(row.tagIds ?? []) } : row,
            ),
          }
        : old,
  );
  queryClient.setQueriesData<{ candidates: CandidateRow[] }>(
    { predicate: (q) => isCandidatesQuery(q.queryKey) },
    (old) =>
      old
        ? {
            ...old,
            candidates: old.candidates.map((row) =>
              row.tikTokCreatorOpenId === tiktokCreatorOpenId ? { ...row, tagIds: update(row.tagIds ?? []) } : row,
            ),
          }
        : old,
  );
};

/**
 * Commit a staged add/remove tag diff across one or many creators via the shared
 * `POST /creators` endpoint. The inline picker passes a single open id; the
 * multi-select action bar passes the whole selection. Optimistic: cached row
 * pills update immediately and revert (with a toast) on failure, then reconcile
 * against the server's authoritative response. Rows not currently cached (e.g.
 * off-page selections) simply no-op and are reconciled by the tag-list refresh.
 */
export const useSaveCreatorTagAssignments = () => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useMutation({
    mutationFn: async (diff: { tiktokCreatorOpenIds: string[]; add: string[]; remove: string[] }) => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].creators.$post({
            param: { workspaceId, merchantId },
            json: diff,
          })
        : await client.merchant[":merchantId"]["creator-tags"].creators.$post({
            param: { merchantId },
            json: diff,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onMutate: async (diff) => {
      await queryClient.cancelQueries({ predicate: (q) => isTaggableRowQuery(q.queryKey) });
      const previous = queryClient.getQueriesData({ predicate: (q) => isTaggableRowQuery(q.queryKey) });

      const removeSet = new Set(diff.remove);
      for (const tiktokCreatorOpenId of diff.tiktokCreatorOpenIds) {
        patchCreatorRowTagIds(tiktokCreatorOpenId, (ids) => {
          const kept = ids.filter((id) => !removeSet.has(id));
          const added = diff.add.filter((id) => !kept.includes(id));
          return [...kept, ...added];
        });
      }

      return { previous };
    },
    onError: (_err, _diff, ctx) => {
      // Roll back the optimistic patch. The error toast (with the server's
      // message) is shown by the global MutationCache handler.
      ctx?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: ({ creators }) => {
      for (const row of creators) {
        patchCreatorRowTagIds(row.tiktokCreatorOpenId, () => row.tagIds);
      }
    },
    onSettled: () => {
      // Refresh the master tag list so assigned counts stay accurate.
      queryClient.invalidateQueries({ queryKey: ["creator-tags", merchantId, workspaceId], exact: true });
    },
  });
};

/**
 * Create-on-the-fly: atomically create a tag (auto-colour, source=manual) and
 * assign it to the creator in one request. The new tag appears as a pill on the
 * row and is added to the merchant tag list.
 */
export const useCreateAndAssignCreatorTag = (tiktokCreatorOpenId: string) => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useMutation({
    mutationFn: async (payload: { name: string; color: CreatorTagColor }) => {
      const json = { ...payload, tiktokCreatorOpenIds: [tiktokCreatorOpenId] };
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].$post({
            param: { workspaceId, merchantId },
            json,
          })
        : await client.merchant[":merchantId"]["creator-tags"].$post({
            param: { merchantId },
            json,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (tag) => {
      patchCreatorRowTagIds(tiktokCreatorOpenId, (ids) => (ids.includes(tag.id) ? ids : [...ids, tag.id]));
      queryClient.invalidateQueries({ queryKey: ["creator-tags", merchantId, workspaceId], exact: true });
    },
    // Errors (e.g. a duplicate name) are surfaced by the global MutationCache handler.
  });
};

// ---------------------------------------------------------------------------
// Bulk tag assignment from the multi-select action bar (UNB-1738)
//
// Assignment itself reuses `useSaveCreatorTagAssignments` (same `POST /creators`
// endpoint) with the full selection. The two hooks below are read/create
// helpers specific to the bulk-apply modal.
// ---------------------------------------------------------------------------

/**
 * Per-tag assignment counts across the selected creators, for the bulk-apply
 * modal's tri-state checkboxes. Only runs while the modal is open.
 */
export const useCreatorTagCounts = (tiktokCreatorOpenIds: string[], enabled = true) => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();
  // Stable key regardless of selection order.
  const sortedIds = [...tiktokCreatorOpenIds].sort();

  return useQuery({
    queryKey: ["creator-tag-counts", merchantId, workspaceId, sortedIds],
    queryFn: async () => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].creators["tag-counts"].$post({
            param: { workspaceId, merchantId },
            json: { tiktokCreatorOpenIds },
          })
        : await client.merchant[":merchantId"]["creator-tags"].creators["tag-counts"].$post({
            param: { merchantId },
            json: { tiktokCreatorOpenIds },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!merchantId && tiktokCreatorOpenIds.length > 0,
  });
};

/**
 * Create-on-the-fly from the bulk-apply modal: create a tag (auto-colour,
 * source=manual) without assigning it. The modal stages it as checked and the
 * single bulk save assigns it to the selection.
 */
export const useCreateCreatorTag = () => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useMutation({
    mutationFn: async (payload: { name: string; color: CreatorTagColor }) => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-tags"].$post({
            param: { workspaceId, merchantId },
            json: payload,
          })
        : await client.merchant[":merchantId"]["creator-tags"].$post({
            param: { merchantId },
            json: payload,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-tags", merchantId, workspaceId], exact: true });
    },
    // Errors (e.g. a duplicate name) are surfaced by the global MutationCache handler.
  });
};
